import React, { useState, useCallback } from 'react';
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { useTranslation } from 'react-i18next';
import { useSetDefaultVersion } from 'hooks/useSetDefaultVersion';
import { useDeleteLessonVersion } from 'hooks/useDeleteLessonVersion';
import {
    LabelTypography,
    LanguageSelect,
    VersionSelectorContainer
} from "components/views/editor/components/VersionSelector.styles";
import { IGetLessonWithVersionsResponse } from "api/models/response/IGetLessonWithVersionsResponse";
import { LessonVersionBase, VrLessonVersion } from "models/LessonVersion";
import { LessonType } from "models/LessonType";
import { AddLessonVersionDialog } from "components/views/editor/components/AddLessonVersionDialog";
import { ManageLessonVersionsDialog } from "components/views/editor/components/ManageLessonVersionsDialog";
import { useSnackbar } from "notistack";


interface VersionSelectorProps {
    lesson: IGetLessonWithVersionsResponse;
    currentLanguage: string;
    pendingNewVersion: string | null;
    onLanguageChange: (language: string) => void;
    onRefresh: () => void;
    onAddNewVersion: (language: string) => void;
}

export const VersionSelector: React.FC<VersionSelectorProps> = ({
                                                                    lesson,
                                                                    currentLanguage,
                                                                    pendingNewVersion,
                                                                    onLanguageChange,
                                                                    onRefresh,
                                                                    onAddNewVersion
                                                                }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openManageDialog, setOpenManageDialog] = useState(false);

    const { setDefaultVersion, setting } = useSetDefaultVersion();
    const { deleteLessonVersion, deleting } = useDeleteLessonVersion();

    const currentVersion = lesson.versions.find(v => v.language === currentLanguage);
    const isCurrentDefault = currentVersion?.isDefault || false;
    const isCurrentPending = pendingNewVersion === currentLanguage;
    const allVersions: (LessonVersionBase | { language: string; isDefault: boolean; isPending: boolean })[] = [
        ...lesson.versions,
        ...(pendingNewVersion ? [{
            language: pendingNewVersion,
            isDefault: false,
            isPending: true
        }] : [])
    ];

    const handleAddVersion = useCallback((language: string) => {
        const languageExists = allVersions.some(v => v.language === language.toLowerCase());
        if (languageExists) {
            enqueueSnackbar(t('versionSelector.languageExists'), { variant: 'error' });
            return;
        }

        setOpenAddDialog(false);
        onAddNewVersion(language);
    }, [allVersions, enqueueSnackbar, t, onAddNewVersion]);

    const switchToDefaultLanguageIfNeeded = useCallback((deletedLanguage: string) => {
        if (deletedLanguage === currentLanguage) {
            const defaultVersion = lesson.versions.find(v => v.isDefault);
            if (defaultVersion) {
                onLanguageChange(defaultVersion.language);
            }
        }
    }, [currentLanguage, lesson.versions, onLanguageChange]);

    const handleSetDefault = useCallback(async (versionId: string) => {
        const response = await setDefaultVersion({
            lessonId: lesson.lessonId,
            versionId: versionId
        });

        if (response?.success) {
            onRefresh();
        }
    }, [setDefaultVersion, lesson.lessonId, onRefresh]);

    const handleDeleteVersion = useCallback(async (language: string) => {
        const version = lesson.versions.find(v => v.language === language);
        if (version?.isDefault) {
            enqueueSnackbar(t('versionSelector.cannotDeleteDefault'), { variant: 'error' });
            return;
        }

        if (!window.confirm(t('versionSelector.confirmDelete', { language }))) {
            return;
        }

        const response = await deleteLessonVersion({
            lessonId: lesson.lessonId,
            language
        });

        if (response?.success) {
            switchToDefaultLanguageIfNeeded(language);
            onRefresh();
        }
    }, [lesson.versions, lesson.lessonId, enqueueSnackbar, t, deleteLessonVersion, switchToDefaultLanguageIfNeeded, onRefresh]);

    return (
        <>
            <VersionSelectorContainer>
                <LabelTypography>
                    {t('versionSelector.language')}:
                </LabelTypography>

                <LanguageSelect
                    value={currentLanguage}
                    onChange={(e) => onLanguageChange(e.target.value as string)}
                    size="small"
                >
                    {allVersions.map((version) => (
                        <MenuItem key={version.language} value={version.language}>
                            <Box display="flex" alignItems="center" gap={1}>
                                {version.language.toUpperCase()}
                                {version.isDefault && (
                                    <StarIcon fontSize="small" color="primary"/>
                                )}
                                {(version as any).isPending && (
                                    <FiberNewIcon fontSize="small" color="secondary"/>
                                )}
                            </Box>
                        </MenuItem>
                    ))}
                </LanguageSelect>

                {isCurrentDefault && (
                    <Chip
                        label={t('versionSelector.default')}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                )}

                {isCurrentPending && (
                    <Chip
                        label={t('versionSelector.unsaved')}
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                )}

                <Tooltip title={t('versionSelector.addVersion')}>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setOpenAddDialog(true)}
                    >
                        <AddIcon/>
                    </IconButton>
                </Tooltip>

                <Tooltip title={t('versionSelector.manageVersions')}>
                    <IconButton
                        size="small"
                        onClick={() => setOpenManageDialog(true)}
                    >
                        <SettingsIcon/>
                    </IconButton>
                </Tooltip>

                <Tooltip title={t('versionSelector.refresh')}>
                    <IconButton
                        size="small"
                        onClick={onRefresh}
                    >
                        <RefreshIcon/>
                    </IconButton>
                </Tooltip>
            </VersionSelectorContainer>

            <AddLessonVersionDialog
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                lesson={lesson}
                onAdd={handleAddVersion}
            />

            <ManageLessonVersionsDialog
                open={openManageDialog}
                onClose={() => setOpenManageDialog(false)}
                lesson={lesson}
                onSetDefault={handleSetDefault}
                onDelete={handleDeleteVersion}
                setting={setting}
                deleting={deleting}
            />
        </>
    );
};